import { Href, useLocalSearchParams, useRouter } from 'expo-router';
import useUpsertFlowStep from 'features/onboarding/hooks/useUpsertFlowStep';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import HyperlinkXsText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkXSText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import useIsFeatureEnabled from 'shared/hooks/statsig/useIsFeatureEnabled/useIsFeatureEnabled';
import ActionScreen from 'shared/patterns/vxl/ActionScreen/ActionScreen';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { openCVSLink } from 'shared/utils/depositCash';

const DEPOSIT_CASH_ROUTE = '/move-money/deposit-cash/map';

const INSTRUCTION_ITEMS = [
  {
    id: 'barcode-instruction',
    translationKey: 'depositCash.howToDeposit.bullet.one',
    iconKey: 'BARCODE',
    iconAccessibilityLabel: 'Barcode Icon',
  },
  {
    id: 'card-instruction',
    translationKey: 'depositCash.howToDeposit.bullet.two',
    iconKey: 'CARD',
    iconAccessibilityLabel: 'Card Icon',
  },
] as const;

const DepositCashScreen = () => {
  const router = useRouter();
  const { submitUpsertFlowStep } = useUpsertFlowStep();
  const { flowId, source } = useLocalSearchParams<{
    flowId?: string;
    source?: string;
  }>();
  const { t: translatedText } = useTranslation('onboarding');
  const isKrogerBarcodeEnabled = useIsFeatureEnabled(
    FeatureFlagKey.ENABLE_GD_BARCODE_KROGER,
  );

  useViewDidFocusScreenAnalyticsEvent({
    key: '/onboarding/deposit-cash/depositCashLocationFlow',
  });

  const seeLocationsBtnAnalytics = usePressableAnalytics({
    analyticsId: 'viewLocationsClicked',
  });

  const handleOnSeeLocationsButtonAction = () => {
    seeLocationsBtnAnalytics();
    if (flowId) {
      void submitUpsertFlowStep({ input: { id: flowId } });
    } else if (source === 'progress-tracker-funding') {
      router.replace(`${DEPOSIT_CASH_ROUTE}?flowSource=onboarding` as Href);
      return;
    }
    router.push(`${DEPOSIT_CASH_ROUTE}?flowSource=onboarding`);
  };

  const disclosurePrefix = isKrogerBarcodeEnabled
    ? 'depositCash.disclosureKroger'
    : 'depositCash.disclosure';

  const howToDepositDescriptionKey = isKrogerBarcodeEnabled
    ? 'depositCash.howToDeposit.descriptionKroger'
    : 'depositCash.howToDeposit.description';

  return (
    <View style={styles.container}>
      <NavigationBar title="" primaryOnPress={() => router.back()} />
      <ActionScreen
        visual="ECASH_A"
        titleText={translatedText('depositCash.howToDeposit.heading')}
        titleTextStyle="extra-large"
        bodyContent={
          <>
            <BodyBaseText numberOfLines={0} style={styles.description}>
              {translatedText(howToDepositDescriptionKey)}
            </BodyBaseText>
            {INSTRUCTION_ITEMS.map((item, index) => (
              <React.Fragment key={item.id}>
                {index > 0 && (
                  <View style={styles.hairLineDivider}>
                    <HairLineDivider />
                  </View>
                )}
                <ListItem
                  primaryCopy={translatedText(
                    isKrogerBarcodeEnabled && item.id === 'barcode-instruction'
                      ? 'depositCash.howToDeposit.bullet.oneKroger'
                      : item.translationKey,
                  )}
                  showsChevron={false}
                  leadingAccessory={{
                    id: item.id,
                    name: VXLComponentId.ICON,
                    props: {
                      iconKey: item.iconKey,
                      iconAccessibilityLabel: item.iconAccessibilityLabel,
                    },
                  }}
                />
              </React.Fragment>
            ))}
            <View style={styles.disclosure}>
              <BodyXSText numberOfLines={0} style={styles.disclosureText}>
                {translatedText(`${disclosurePrefix}.first`)}
                <HyperlinkXsText onPress={openCVSLink}>
                  {translatedText(`${disclosurePrefix}.url`)}
                </HyperlinkXsText>
              </BodyXSText>
              <BodyXSText numberOfLines={0} style={styles.disclosureText}>
                {translatedText(`${disclosurePrefix}.second`)}
              </BodyXSText>
              <BodyXSText numberOfLines={0} style={styles.disclosureText}>
                {translatedText(`${disclosurePrefix}.third`)}
              </BodyXSText>
            </View>
          </>
        }
        primaryButton={{
          label: translatedText('depositCash.buttons.seeLocations'),
          onPress: handleOnSeeLocationsButtonAction,
          analyticsId: 'viewLocationsClicked',
        }}
        includeDefaultPadding={false}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  description: {
    paddingHorizontal: theme.spacing.m,
  },
  hairLineDivider: {
    paddingHorizontal: theme.spacing.m,
  },
  disclosure: {
    flexDirection: 'column',
    paddingHorizontal: theme.spacing.m,
    paddingBottom: theme.spacing.m,
  },
  disclosureText: {
    paddingTop: theme.spacing.m,
    color: theme.color.text.secondary,
  },
});

export default DepositCashScreen;
